import React from "react";
import { useLayoutEffect } from "react";

// Боковое меню навигирует нативным скроллом (fullpage-Swiper отключён).
// Логотип -> вверх страницы; кнопки -> к секциям по id.
const SECTION_IDS = ["#about", "#cases", "#work", "#expertise", "#when", "#team", "#partners"];
const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);

export default function AsideMenu() {
  useLayoutEffect(() => {
    const aside = document.querySelector(".aside");
    const asideBtns = document.querySelectorAll(".js-aside-btn");
    // asideBtns[0] — логотип, дальше пункты идут в порядке SECTION_IDS
    const items = Array.from(asideBtns).map((btn) => btn.closest(".aside__item"));

    const setActive = (activeIndex) => {
      SECTION_IDS.forEach((id, i) => {
        const item = items[i + 1];
        if (!item) return;
        item.classList.toggle("active", i === activeIndex);
      });
    };

    const clickHandlers = [];
    asideBtns.forEach((btn, index) => {
      const onClick = () => {
        if (index === 0) {
          window.scrollTo({ top: 0, behavior: "smooth" });
          return;
        }
        const target = document.querySelector(SECTION_IDS[index - 1]);
        if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
      };
      btn.addEventListener("click", onClick);
      clickHandlers.push([btn, onClick]);
    });

    // Скролл-спай: подсветка активного пункта, тема light на тёмной секции work,
    // скрытие меню на герое (верх) и внизу (после «Клиенты»).
    // Секции тянутся из CMS асинхронно, поэтому ищем их на каждом скролле.
    const onScroll = () => {
      const body = document.querySelector("body");
      const mid = window.innerHeight / 2;
      let current = -1;
      SECTION_IDS.forEach((id, i) => {
        const sec = document.querySelector(id);
        if (!sec) return;
        const rect = sec.getBoundingClientRect();
        if (rect.top <= mid && rect.bottom >= mid) current = i;
      });

      // Плавная смена цвета фона страницы (CSS transition на body) под активную
      // секцию: work -> тёмный, «Экспертиза» -> серый (slate). Десктоп only —
      // на мобиле у этих секций свой фон.
      const activeId = current !== -1 ? SECTION_IDS[current] : null;
      const isWork = activeId === "#work";
      const isExpertise = activeId === "#expertise";
      const isDesktop = window.matchMedia("(min-width: 1100px)").matches;
      body.classList.toggle("section-dark", isWork && isDesktop);
      body.classList.toggle("section-slate", isExpertise && isDesktop);

      // Пока первый экран (герой) ещё виден — меню скрыто. Герой грузит
      // контент из CMS асинхронно и до этого не рендерит .header вовсе —
      // если элемента ещё нет в DOM, считаем герой видимым (меню держим
      // скрытым), а не наоборот, иначе на старте страницы меню на долю
      // секунды мелькает, пока герой не домонтируется.
      const hero = document.querySelector(".header");
      const heroVisible = !hero || hero.getBoundingClientRect().bottom > 0;

      // После последней отслеживаемой секции (Партнёры) меню не прячется
      // рывком, а плавно уезжает наверх ВМЕСТЕ с контентом блока: как только
      // низ «Клиентов» оказывается ближе одного экрана к верху вьюпорта,
      // на меню накладывается translateY, синхронный со скроллом — к тому
      // моменту, как блок долистает до конца, меню уже само уйдёт за край.
      const lastIndex = SECTION_IDS.length - 1;
      const lastSection = document.querySelector(SECTION_IDS[lastIndex]);
      const lastRect = lastSection && lastSection.getBoundingClientRect();
      const viewportH = window.innerHeight;
      let exitProgress = 0;
      if (lastRect) {
        exitProgress = clamp((viewportH - lastRect.bottom) / viewportH, 0, 1);
      }
      if (current === -1 && exitProgress > 0 && exitProgress < 1) {
        current = lastIndex;
      }
      aside.style.transform = exitProgress > 0 ? `translateY(${-exitProgress * viewportH}px)` : "";

      if ((current === -1 && exitProgress >= 1) || heroVisible) {
        aside.classList.add("none");
        return;
      }
      if (!body.classList.contains("cases-modal-open")) {
        aside.classList.remove("none");
      }
      setActive(current);
      // Светлая тема меню на тёмной (work) и серой (Экспертиза) секциях;
      // на slate-секции — отдельные цвета по макету (зелёная активная линия).
      aside.classList.toggle("light", isWork || isExpertise);
      aside.classList.toggle("slate", isExpertise);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();

    // Надёжная блокировка: IntersectionObserver на герое. Срабатывает всякий раз,
    // когда первый экран входит/выходит из кадра, независимо от scroll-событий, —
    // чтобы меню гарантированно скрывалось на первом экране. Герой грузится асинхронно,
    // поэтому ждём его появления.
    let io;
    let raf;
    const attachHeroObserver = () => {
      const hero = document.querySelector(".header");
      if (!hero) return false;
      io = new IntersectionObserver(() => onScroll(), { threshold: [0, 0.01] });
      io.observe(hero);
      return true;
    };
    if (!attachHeroObserver()) {
      let tries = 0;
      const retry = () => {
        if (attachHeroObserver() || tries++ > 120) return;
        raf = requestAnimationFrame(retry);
      };
      raf = requestAnimationFrame(retry);
    }

    // Секции монтируются по мере ответа CMS — это не scroll и не resize,
    // поэтому ничего не пересчитывает состояние меню/фона до следующего
    // скролла пользователя. Если он не скроллит (например, зашёл сразу на
    // якорь секции), body может навсегда остаться без тёмного фона, а текст
    // — белым по белому. Досматриваем DOM: как только очередная секция
    // появляется/меняется, пересчитываем немедленно.
    const domObserver = new MutationObserver(() => onScroll());
    domObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (io) io.disconnect();
      if (raf) cancelAnimationFrame(raf);
      domObserver.disconnect();
      clickHandlers.forEach(([btn, fn]) => btn.removeEventListener("click", fn));
    };
  }, []);

  return (
    <aside className="aside none">
      <div className="container">
        <nav className="aside__nav">
          <button className="aside__logo js-aside-btn">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="16"
              viewBox="0 0 20 16"
              fill="none"
            >
              <path
                d="M2.64223 0.0117188H0V15.984H2.64223V0.0117188Z"
                fill="#101010"
              />
              <path
                d="M12.0864 8.06648L10.7013 10.7771L8.03264 15.9883H5.1875L9.32508 8.08792L5.1875 0.0117188H7.87384L10.7146 5.43732L12.0864 8.06648Z"
                fill="#101010"
              />
              <path
                d="M13.0967 8.0782L14.4862 10.7889L17.1549 16H20L15.8624 8.09964L20 0.0234375H17.3137L14.4729 5.44904L13.0967 8.0782Z"
                fill="#101010"
              />
            </svg>
          </button>
          <div className="aside__list">
            <li className="aside__item">
              <span></span>
              <button className="aside__btn js-aside-btn">Мы</button>
            </li>
            <li className="aside__item">
              <span></span>
              <button className="aside__btn js-aside-btn">Кейсы</button>
            </li>
            <li className="aside__item">
              <span></span>
              <button className="aside__btn js-aside-btn">
                Как мы работаем
              </button>
            </li>
            <li className="aside__item">
              <span></span>
              <button className="aside__btn js-aside-btn">Экспертиза</button>
            </li>
            <li className="aside__item">
              <span></span>
              <button className="aside__btn js-aside-btn">
                Когда звать нас
              </button>
            </li>
            <li className="aside__item">
              <span></span>
              <button className="aside__btn js-aside-btn">Команда</button>
            </li>
            <li className="aside__item">
              <span></span>
              <button className="aside__btn js-aside-btn">Клиенты</button>
            </li>
          </div>
        </nav>
      </div>
    </aside>
  );
}
